// The ratings API (docs/12 "Usage and ratings", row S73): a workflow's reviews as users and as
// moderators see them, hiding and unhiding one, the platform's rating weights, and a task's
// rating by its owner. Keys start with "ratings" except the per-workflow ones, which sit under
// the workflow's catalog key so a change refreshes its stats with them.
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

import { api } from "../api/client";
import { ApiError, unwrap } from "../api/queries";
import { parseRatingWeights, REVIEW_PAGE, type Weights } from "./model";

const workflowPath = (id: string) => ({ workflow_id: id });

/**
 * Pages of reviews, newest first, each continuing after the last entry's `cursor` (S79). A page
 * shorter than the limit is the last.
 */
const pages = <T extends { cursor: string }>(key: unknown[], fetchPage: (before: string | undefined) => Promise<T[]>) =>
  infiniteQueryOptions({
    queryKey: key,
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: T[]) => (last.length < REVIEW_PAGE ? undefined : last[last.length - 1]?.cursor),
  });

const page = (before: string | undefined) => ({ limit: REVIEW_PAGE, ...(before ? { before } : {}) });

/** Written, unhidden reviews; none while the workflow is not in the marketplace (404). */
export const reviewsQuery = (workflowId: string) =>
  pages(["catalog", "workflows", workflowId, "reviews", "public"], async (before) => {
    try {
      return await unwrap(api.GET("/api/marketplace/workflows/{workflow_id}/reviews", { params: { path: workflowPath(workflowId), query: page(before) } }));
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return [];
      throw e;
    }
  });

/** Every rating, hidden or not, with or without text (ModerateWorkflowReviews). */
export const moderationQuery = (workflowId: string) =>
  pages(["catalog", "workflows", workflowId, "reviews", "moderation"], (before) =>
    unwrap(api.GET("/api/admin/workflows/{workflow_id}/reviews", { params: { path: workflowPath(workflowId), query: page(before) } })),
  );

export function setReviewHidden(workflowId: string, ratingId: string, hidden: boolean) {
  const params = { params: { path: { ...workflowPath(workflowId), rating_id: ratingId } } };
  return hidden
    ? unwrap(api.POST("/api/admin/workflows/{workflow_id}/reviews/{rating_id}/hide", params))
    : unwrap(api.POST("/api/admin/workflows/{workflow_id}/reviews/{rating_id}/unhide", params));
}

export const ratingWeightsQuery = () =>
  queryOptions({
    queryKey: ["ratings", "weights"],
    queryFn: async () => parseRatingWeights(await unwrap(api.GET("/api/admin/settings/rating-weights"))),
  });

/** Throws an ApiError 422 when the API refuses them. */
export const saveRatingWeights = async (weights: Weights) =>
  parseRatingWeights(await unwrap(api.PUT("/api/admin/settings/rating-weights", { body: { weights } })));

const taskPath = (id: string) => ({ params: { path: { task_id: id } } });

export const taskRatingQuery = (taskId: string) =>
  queryOptions({
    queryKey: ["tasks", taskId, "rating"],
    queryFn: () => unwrap(api.GET("/api/tasks/{task_id}/rating", taskPath(taskId))),
  });

export interface RatingInput {
  results: number;
  cost: number;
  /** Blank means none. */
  review: string;
}

export const rateTask = (taskId: string, input: RatingInput) =>
  unwrap(api.PUT("/api/tasks/{task_id}/rating", { ...taskPath(taskId), body: { ...input, review: input.review.trim() || null } }));
